import React, { useRef, useCallback } from 'react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
} from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { Button } from '@/components/ui/button';

interface VideoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Full-screen video lightbox for the "How It Works" overview video.
 * Dark immersive overlay, large close button, CTA below video.
 */
const VideoModal: React.FC<VideoModalProps> = ({ open, onOpenChange }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { openModal } = useEarlyAdopter();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && videoRef.current) {
        videoRef.current.pause();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleCTA = () => {
    onOpenChange(false);
    openModal('Homepage Video CTA');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-4xl translate-x-[-50%] translate-y-[-50%] p-4 sm:p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          {/* Close button, large touch target */}
          <DialogClose className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50">
            <XMarkIcon className="w-6 h-6" />
            <span className="sr-only">Close</span>
          </DialogClose>

          {/* Video */}
          <div className="rounded-md overflow-hidden shadow-2xl bg-black">
            <video
              ref={videoRef}
              className="w-full aspect-video"
              controls
              autoPlay
              playsInline
              preload="metadata"
              src="/video/overview-how-it-works.mp4"
            />
          </div>

          {/* CTA below video */}
          <div className="flex justify-center mt-4">
            <Button
              onClick={handleCTA}
              size="lg"
              className="text-lg px-8 py-6 rounded-sm font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-overlay min-h-[56px]"
            >
              Try It Free
            </Button>
          </div>

          {/* Accessibility: visually hidden title for screen readers */}
          <DialogPrimitive.Title className="sr-only">
            How SeniorThrive Works
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            A short video overview of how SeniorThrive helps you thrive at home.
          </DialogPrimitive.Description>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default VideoModal;
